import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { FieldTypeIcon } from "../../atoms/FieldTypeIcon";
import { currentViewport, placeMenu } from "../../../utils/menuPlacement";
import type { SearchableFieldMenuProps } from "./SearchableFieldMenu.types";
import {
  Backdrop,
  Empty,
  GroupTitle,
  ItemIcon,
  List,
  Option,
  OptionHint,
  OptionLabel,
  Search,
  SearchInput,
  Surface,
} from "./SearchableFieldMenu.styles";

/** A stable ref callback: focus runs once on mount, so typing starts at once and is never interrupted. */
const focusOnMount = (node: HTMLElement | null) => node?.focus();

/** The option after (`step` 1) or before (`step` -1) the active one, wrapping; `null` when none can be chosen. */
const stepActive = (ids: string[], activeId: string | null, step: 1 | -1): string | null => {
  if (ids.length === 0) return null;
  const at = activeId === null ? -1 : ids.indexOf(activeId);
  if (at === -1) return step === 1 ? ids[0] : ids[ids.length - 1];
  return ids[(at + step + ids.length) % ids.length];
};

/**
 * Grouped options under a search box, opened under an anchor. Generic: a field picker, a
 * sort-key picker, a drill-down list. The caller narrows `groups` to `query`.
 */
const OpenMenu: React.FC<SearchableFieldMenuProps & { anchor: NonNullable<SearchableFieldMenuProps["anchor"]> }> = ({
  anchor,
  groups,
  query,
  activeId,
  copy,
  searchIcon,
  onQueryChange,
  onActiveChange,
  onChoose,
  onClose,
}) => {
  // Focus goes back to whatever opened the menu (the "+") when it closes.
  const opener = useRef<Element | null>(typeof document === "undefined" ? null : document.activeElement);
  useEffect(
    () => () => {
      const node = opener.current;
      if (node instanceof HTMLElement && node.isConnected) node.focus();
    },
    [],
  );
  const placement = placeMenu(anchor, currentViewport(), { width: 256 });
  const chooseable = groups.flatMap((group) => group.options.filter((option) => !option.disabled).map((option) => option.id));
  const isEmpty = groups.every((group) => group.options.length === 0);
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Escape") {
      event.preventDefault();
      onClose();
    } else if (event.key === "Tab") {
      // Tab leaves the menu: close it, and focus returns to the opener.
      onClose();
    } else if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      onActiveChange(stepActive(chooseable, activeId, event.key === "ArrowDown" ? 1 : -1));
    } else if (event.key === "Enter") {
      event.preventDefault();
      const target = activeId !== null && chooseable.includes(activeId) ? activeId : chooseable[0];
      if (target !== undefined) onChoose(target);
    }
  };
  return createPortal(
    <>
      <Backdrop data-testid="searchable-field-menu-backdrop" onClick={onClose} />
      <Surface
        role="dialog"
        aria-label={copy.label}
        data-testid="searchable-field-menu"
        $left={placement.left}
        $top={placement.top}
        $bottom={placement.bottom}
        $maxHeight={placement.maxHeight}
        onKeyDown={handleKeyDown}
      >
        <Search>
          {searchIcon}
          <SearchInput
            ref={focusOnMount}
            role="combobox"
            aria-expanded="true"
            aria-controls="searchable-field-menu-list"
            aria-activedescendant={activeId ? `searchable-field-option-${activeId}` : undefined}
            aria-label={copy.placeholder}
            placeholder={copy.placeholder}
            value={query}
            onChange={(event) => onQueryChange(event.target.value)}
          />
        </Search>
        <List id="searchable-field-menu-list" role="listbox" aria-label={copy.label}>
          {isEmpty ? <Empty role="status">{copy.empty}</Empty> : null}
          {groups
            .filter((group) => group.options.length > 0)
            .map((group) => (
              <div key={group.id} role="group" aria-label={group.title}>
                {group.title ? <GroupTitle>{group.title}</GroupTitle> : null}
                {group.options.map((option) => (
                  <Option
                    key={option.id}
                    id={`searchable-field-option-${option.id}`}
                    type="button"
                    role="option"
                    aria-selected={option.id === activeId}
                    disabled={option.disabled}
                    $active={option.id === activeId}
                    tabIndex={-1}
                    onMouseEnter={() => !option.disabled && onActiveChange(option.id)}
                    onClick={() => onChoose(option.id)}
                  >
                    {option.icon ?? option.iconKind ? (
                      <ItemIcon>{option.icon ?? <FieldTypeIcon kind={option.iconKind!} size={14} />}</ItemIcon>
                    ) : null}
                    <OptionLabel>{option.label}</OptionLabel>
                    {option.hint ? <OptionHint>{option.hint}</OptionHint> : null}
                  </Option>
                ))}
              </div>
            ))}
        </List>
      </Surface>
    </>,
    document.body,
  );
};

export const SearchableFieldMenu: React.FC<SearchableFieldMenuProps> = ({ anchor, ...rest }) =>
  anchor ? <OpenMenu anchor={anchor} {...rest} /> : null;

export default SearchableFieldMenu;
